
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Language, LanguageContextType } from '@/i18n/types';
import { translate, saveLanguagePreference, getLanguagePreference } from '@/i18n/utils';

// Create a default context value to prevent undefined errors during hot reload
const defaultContextValue: LanguageContextType = {
  language: 'hu' as Language,
  t: (key: string) => key, // fallback that returns the key
  toggleLanguage: () => {},
  setLanguage: () => {}
};

const LanguageContext = createContext<LanguageContextType>(defaultContextValue);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Get language from localStorage or browser settings using the utility function
  const [language, setLanguage] = useState<Language>(() => getLanguagePreference());

  // Save language preference to localStorage when it changes
  useEffect(() => {
    saveLanguagePreference(language);
  }, [language]);

  // Translation function using the utility
  const t = (key: string, params?: Record<string, string | number>): string => {
    return translate(language, key, params);
  };

  const value: LanguageContextType = {
    language,
    t,
    toggleLanguage: () => setLanguage((prevLanguage) => (prevLanguage === 'hu' ? 'en' : 'hu')),
    setLanguage
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  return context;
};
